import React, { useEffect } from 'react';
import { NavigationProvider, useNavigation } from './context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import { PublicNavbar } from './components/layout/PublicNavbar';
import { PublicFooter } from './components/layout/PublicFooter';
import { HomePage } from './pages/HomePage';
import { TemplatesPage } from './pages/TemplatesPage';
import { SectorsPage } from './pages/SectorsPage';
import { PricingPage } from './pages/PricingPage';
import { FeaturesPage } from './pages/FeaturesPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FaqPage } from './pages/FaqPage';
import { ContactPage } from './pages/ContactPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { KvkkPage } from './pages/KvkkPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { DistanceSalesPage } from './pages/DistanceSalesPage';
import { CookieConsentBanner } from './components/ui/CookieConsentBanner';
import { WorkspaceApp } from '../workspace/WorkspaceApp';
import { AdminLayout } from '../components/admin/AdminLayout';
import { ShieldAlert, ArrowRight, Loader2, Sparkles } from 'lucide-react';

const ProtectedDashboardView: React.FC = () => {
  const { isAuthenticated, isLoading, currentUser, isSuperAdmin } = useAuth();
  const { navigate } = useNavigation();

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        const timer = setTimeout(() => {
          navigate('/giris');
        }, 1200);
        return () => clearTimeout(timer);
      } else if (isSuperAdmin && currentUser?.email?.toLowerCase() === 'admin@adiosoft.com') {
        // STRICT: ONLY admin@adiosoft.com with verified super admin claim is redirected to /admin
        navigate('/admin');
      }
    }
  }, [isLoading, isAuthenticated, isSuperAdmin, currentUser?.email, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-4">
        <div className="w-12 h-12 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-4">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
        </div>
        <p className="text-sm font-semibold text-slate-300">Oturum bilgisi doğrulanıyor...</p>
      </div>
    );
  }

  if (!isAuthenticated || !currentUser) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-4 text-center">
        <div className="w-16 h-16 rounded-3xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mb-4">
          <ShieldAlert className="w-8 h-8 text-rose-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Erişim Kısıtlaması</h2>
        <p className="text-sm text-slate-400 max-w-md mb-6 leading-relaxed">
          Müşteri paneline erişmek için lütfen kullanıcı hesabınızla giriş yapınız.
        </p>
        <button
          onClick={() => navigate('/giris')}
          className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold shadow-lg shadow-blue-600/30 transition flex items-center gap-2"
        >
          <span>Giriş Yap</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // If super admin landed on customer dashboard, render redirection message
  if (isSuperAdmin && currentUser?.email?.toLowerCase() === 'admin@adiosoft.com') {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-100 p-4 text-center">
        <div className="w-12 h-12 rounded-2xl bg-rose-600/20 border border-rose-500/30 flex items-center justify-center mb-4">
          <Loader2 className="w-6 h-6 text-rose-400 animate-spin" />
        </div>
        <p className="text-sm font-semibold text-slate-300">Sistem Yönetimi paneline yönlendiriliyorsunuz...</p>
      </div>
    );
  }

  return <WorkspaceApp onReturnToMarketing={() => navigate('/')} />;
};

const PublicPageRouter: React.FC = () => {
  const { currentPath } = useNavigation();

  if (currentPath.startsWith('/admin')) {
    return <AdminLayout />;
  }

  if (currentPath.startsWith('/dashboard')) {
    return <ProtectedDashboardView />;
  }

  const renderCurrentPage = () => {
    switch (currentPath) {
      case '/':
        return <HomePage />;
      case '/temalar':
        return <TemplatesPage />;
      case '/sektorler':
        return <SectorsPage />;
      case '/fiyatlandirma':
        return <PricingPage />;
      case '/ozellikler':
        return <FeaturesPage />;
      case '/nasil-calisir':
        return <HowItWorksPage />;
      case '/sss':
        return <FaqPage />;
      case '/iletisim':
        return <ContactPage />;
      case '/giris':
        return <LoginPage />;
      case '/kayit':
        return <RegisterPage />;
      case '/kullanim-kosullari':
        return <TermsPage />;
      case '/gizlilik-politikasi':
        return <PrivacyPolicyPage />;
      case '/kvkk-aydinlatma-metni':
        return <KvkkPage />;
      case '/cerez-politikasi':
        return <CookiePolicyPage />;
      case '/mesafeli-satis-ve-on-bilgilendirme':
        return <DistanceSalesPage />;
      default:
        return <HomePage />;
    }
  };

  const isAuthPage = currentPath === '/giris' || currentPath === '/kayit';

  return (
    <div className="min-h-screen w-full max-w-full bg-slate-950 text-slate-100 flex flex-col font-sans antialiased selection:bg-blue-600 selection:text-white overflow-x-hidden">
      {!isAuthPage && <PublicNavbar />}
      <main className="flex-1 w-full max-w-full overflow-x-hidden">{renderCurrentPage()}</main>
      {!isAuthPage && <PublicFooter />}
      <CookieConsentBanner />
    </div>
  );
};

export const PublicWebsiteApp: React.FC = () => {
  return (
    <NavigationProvider>
      <PublicPageRouter />
    </NavigationProvider>
  );
};

export default PublicWebsiteApp;
